/**
 * A region on the page, and the header row that names it.
 *
 * Every block on this product used to bring its own panel: its own radius, its
 * own padding, its own border, its own heading size. The result was a screen
 * where nothing weighed more than anything else, because the only layout
 * primitive was "a box". This is that box, written once, so that weight can be
 * carried by what a region contains and where it sits rather than by how its
 * container happens to be styled.
 *
 * The header is optional and deliberately short: one line, a label, and at most
 * one trailing control. A region that needs two controls is two regions.
 */

type PanelProps = {
  readonly title?: string | undefined;
  /** One control or timestamp, right-aligned in the header. */
  readonly trailing?: React.ReactNode | undefined;
  /** Drop the body padding, for a panel whose content is a full-bleed table. */
  readonly flush?: boolean;
  readonly labelledBy?: string | undefined;
  readonly testId?: string | undefined;
  readonly variant?: "default" | "analytics";
  /** A small status dot before the title, in a status or pool colour token. */
  readonly dot?: string | undefined;
  readonly children: React.ReactNode;
};

/**
 * The header bar and title of every panel, including ones that compose their
 * own header. Six components had copied the bar by hand, so a change to the
 * design system never reached them; they import these instead.
 */
export const PANEL_HEADER =
  "flex min-h-11 items-center border-b border-border-subtle bg-background-base px-4";
export const PANEL_TITLE = "text-body-lg font-semibold text-text-primary";

export function Panel({
  title,
  trailing,
  flush = false,
  labelledBy,
  testId,
  variant = "default",
  dot,
  children,
}: PanelProps): React.JSX.Element {
  return (
    // One radius and no shadow, whichever variant this is. The analytics
    // variant used to be lifted, which made two panels on one page look like
    // two products; the variant now changes only the header, which is the
    // thing it was actually for.
    <section
      aria-labelledby={labelledBy}
      data-testid={testId}
      data-variant={variant}
      className="overflow-hidden rounded-panel border border-border-subtle bg-surface-default"
    >
      {title !== undefined && (
        <SectionHeader
          title={title}
          trailing={trailing}
          id={labelledBy}
          variant={variant}
          dot={dot}
        />
      )}
      <div className={flush ? "" : "p-4"}>{children}</div>
    </section>
  );
}

type HeaderProps = {
  readonly title: string;
  readonly trailing?: React.ReactNode;
  readonly id?: string | undefined;
  readonly variant?: "default" | "analytics";
  readonly dot?: string | undefined;
};

/**
 * The line that names a region.
 *
 * A label rather than a heading size, because a screen with eight regions and
 * eight bold headings has eight things shouting and nothing leading. The
 * heading level is still there for a screen reader; it simply does not need to
 * be the loudest thing in the room.
 */
export function SectionHeader({
  title,
  trailing,
  id,
  dot,
}: HeaderProps): React.JSX.Element {
  // One header for every panel on every screen. The analytics variant had a
  // taller bar and a sentence-case sans title, so two panels side by side
  // announced themselves in two different voices.
  return (
    // The header bar takes its own ground rather than relying on the
    // hairline alone. At border-subtle's new 1.22:1 weight a rule cannot
    // divide a panel by itself, and it is not asked to: the change of surface
    // does the dividing, which is what §5 prefers everywhere else.
    // The panel's name in the body-lg role (§3), the way the approved mock
    // draws it: a 12px grey caption made every panel read as a footnote.
    <div className={`${PANEL_HEADER} justify-between gap-3`}>
      <h2 id={id} className={`${PANEL_TITLE} flex items-center gap-2`}>
        {dot !== undefined && <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${dot}`} />}
        {title}
      </h2>
      {trailing !== undefined && <div className="flex items-center gap-2">{trailing}</div>}
    </div>
  );
}
